import * as Sentry from '@sentry/angular';
import { environment } from '@environment/environment';

// One Sentry release per deploy, shared by the four apps (they build from
// the same commit): `vitay-frontend@<package version>+<short commit sha>`.
export const sentryRelease = (version: string, sha: string): string =>
  `vitay-frontend@${version}+${sha}`;

// Plain object wrapper so tests can spy on these.
// @sentry/angular v8 uses ESM-only exports that are non-configurable,
// making spyOn / Object.defineProperty impossible to use in Jasmine tests.
export const sentryHelper = {
  captureException: (error: any, context?: any) =>
    Sentry.captureException(error, context),
  addBreadcrumb: (breadcrumb: any) => Sentry.addBreadcrumb(breadcrumb)
};

// Angular reports a request the browser never completed as status 0: the tab
// was closed or navigated away mid-flight, or the connection dropped. For the
// static files under /assets/ that is a client-side condition we can neither
// fix nor act on, and it produced thousands of unactionable Sentry issues
// (VITAY-FRONTEND-Q3, -QP, -WD, -WF).
//
// A genuine problem with an asset — a deploy that dropped the file — arrives
// as a 404, so those stay reportable on purpose.
export const isAbortedAssetRequest = (error: any): boolean => {
  if (!error || error.name !== 'HttpErrorResponse' || error.status !== 0) {
    return false;
  }

  return /(^|\/)assets\//.test(String(error.url || ''));
};

// Single reporting policy for the static assets we load at bootstrap, so a
// browser abort leaves a breadcrumb while anything else still pages us.
export const reportAssetLoadFailure = (error: any, url: string): void => {
  if (isAbortedAssetRequest(error)) {
    sentryHelper.addBreadcrumb({
      category: 'http',
      level: 'warning',
      message: `Asset request did not complete: ${url}`,
      data: { url, status: 0 }
    });

    return;
  }

  sentryHelper.captureException(error, {
    tags: { component: 'AssetLoad' },
    extra: {
      url,
      error_message: error && error.message,
      error_status: error && error.status
    }
  });
};

// ---------------------------------------------------------------------------
// API error reporting
//
// The HTTP interceptor rethrows the API error body (an array of
// {status, title, detail}) or, when there is none, JSON.stringify(err). Both
// are plain values, so anything a component did not catch reached Sentry as
// "Error: [object Object]" (20k events in one issue) or as a message holding
// the whole serialised response, one issue per URL. The helpers below give
// every path a real Error with a stable, id-free message.
// ---------------------------------------------------------------------------

const REPORTED_BY_INTERCEPTOR = '__vitayReportedByInterceptor';

// The interceptor already acted on the response (sent the visitor to /login):
// mark the value it rethrows so the shared beforeSend drops the report that a
// route resolver or subscriber without its own error handler would otherwise
// file (VITAY-FRONTEND-Y0). Strings cannot carry the mark and pass through.
export const markHandledByInterceptor = <T>(value: T): T => {
  if (value && typeof value === 'object') {
    try {
      (value as any)[REPORTED_BY_INTERCEPTOR] = true;
    } catch (e) {
      /* frozen value: nothing to mark */
    }
  }
  return value;
};

const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

// One endpoint is one issue: /candidates/9f…/references → /candidates/:id/references
export const normalizeApiPath = (url: string): string => {
  let path = String(url || '');
  try {
    path = new URL(path, window.location.origin).pathname;
  } catch (e) {
    /* keep the raw value */
  }
  return path.replace(UUID, ':id').replace(/\/\d+(?=\/|$)/g, '/:id');
};

// Validation details embed emails, ids and counts; mask them so one rule is
// one issue and nobody's identity rides along.
export const maskApiDetail = (detail: unknown): string =>
  String(detail == null ? '' : detail)
    .replace(/[\w+.-]+@[\w-]+(?:\.[\w-]+)+/g, '[EMAIL]')
    .replace(UUID, '[ID]')
    .replace(/\d+/g, 'N')
    .trim()
    .slice(0, 200);

// Status 0 (connection dropped, CORS-less 504 from the load balancer, tab
// closed) and 5xx are transport failures: the API cannot have reported them.
export const isTransportFailure = (status: number): boolean =>
  status === 0 || status >= 500;

const httpFailureError = (status: number, method: string, path: string): Error => {
  const error = new Error(`HTTP ${status} ${method} ${path}`);
  error.name = 'HttpFailure';
  return error;
};

// Called by the interceptor for every failed request. 4xx are the API's to
// report (it does, from render_errors); here we only own what never reached
// it. Must never throw: it runs inside the error path of every request.
export const reportHttpFailure = (error: any, method: string): void => {
  try {
    if (!error || error.name !== 'HttpErrorResponse') {
      return;
    }
    if (isAbortedAssetRequest(error)) {
      sentryHelper.addBreadcrumb({
        category: 'http',
        level: 'warning',
        message: `Asset request did not complete: ${error.url}`,
        data: { url: error.url, status: 0 }
      });
      return;
    }
    const status = Number(error.status) || 0;
    if (!isTransportFailure(status)) {
      return;
    }
    // Nothing to fix on our side while the browser itself is offline.
    if (status === 0 && typeof navigator !== 'undefined' && navigator.onLine === false) {
      return;
    }
    const verb = String(method || 'GET').toUpperCase();
    const path = normalizeApiPath(error.url);
    const synthetic: any = httpFailureError(status, verb, path);
    synthetic[REPORTED_BY_INTERCEPTOR] = true;

    sentryHelper.captureException(synthetic, {
      level: status === 0 ? 'warning' : 'error',
      tags: { http_status: String(status), http_method: verb, api_endpoint: path },
      fingerprint: ['http-failure', verb, path, String(status)],
      extra: {
        url: error.url,
        status_text: error.statusText,
        message: error.message
      }
    });
  } catch (e) {
    /* reporting must never break the request pipeline */
  }
};

const unwrapZoneError = (error: any): any =>
  error && typeof error === 'object' && 'rejection' in error && error.rejection !== undefined
    ? error.rejection
    : error && typeof error === 'object' && error.ngOriginalError
      ? error.ngOriginalError
      : error;

const isApiErrorList = (value: any): boolean =>
  Array.isArray(value) &&
  value.length > 0 &&
  value[0] !== null &&
  typeof value[0] === 'object' &&
  ('title' in value[0] || 'detail' in value[0] || 'status' in value[0]);

const parseSerialisedHttpError = (value: any): any => {
  if (typeof value !== 'string' || value.charAt(0) !== '{') {
    return null;
  }
  try {
    const parsed = JSON.parse(value);
    return parsed && parsed.name === 'HttpErrorResponse' ? parsed : null;
  } catch (e) {
    return null;
  }
};

// `extractor` for Sentry.createErrorHandler: turns the interceptor's rethrown
// values back into Errors with a stable message before Sentry sees them.
export const extractApiError = (
  error: unknown,
  defaultExtractor: (error: unknown) => unknown
): unknown => {
  const raw = unwrapZoneError(error);

  if (isApiErrorList(raw)) {
    const first = raw[0];
    const status = Number(first.status) || 0;
    const title = String(first.title || 'API error');
    const detail = maskApiDetail(first.detail);
    const apiError: any = new Error(
      `API ${status || '?'} ${title}${detail ? ` - ${detail}` : ''}`
    );
    apiError.name = 'ApiError';
    if (raw[REPORTED_BY_INTERCEPTOR]) {
      apiError[REPORTED_BY_INTERCEPTOR] = true;
    }
    return apiError;
  }

  const serialised = parseSerialisedHttpError(raw);
  if (serialised) {
    const status = Number(serialised.status) || 0;
    const path = normalizeApiPath(serialised.url);
    const httpError: any = httpFailureError(status, 'HTTP', path);
    httpError.name = 'HttpErrorResponse';
    // Transport failures were already captured by the interceptor with the
    // method and a proper fingerprint; aborted asset loads are not defects.
    if (isTransportFailure(status) || isAbortedAssetRequest(serialised)) {
      httpError[REPORTED_BY_INTERCEPTOR] = true;
    }
    return httpError;
  }

  return defaultExtractor(error);
};

// Shared Sentry.init beforeSend for the four apps.
export const sentryBeforeSend = (event: any, hint: any): any => {
  if (environment.ENVIRONMENT === 'development') {
    return null;
  }
  const original = hint && hint.originalException;
  // A static asset the browser never finished fetching is a client-side
  // condition, not a defect (VITAY-FRONTEND-Q3, -QP, -WD, -WF).
  if (isAbortedAssetRequest(original)) {
    return null;
  }
  // Already captured once, with the request method and endpoint fingerprint,
  // or already handled by the interceptor's redirect to /login.
  if (original && original[REPORTED_BY_INTERCEPTOR]) {
    return null;
  }
  return event;
};
